import { StreamStatusEndpoints, type ViewerClientsResponse } from '@scaleaq/sitecontrol-lib';

import { API_NAMES } from '@/constants';
import { useAuthApiGetSingleFetch } from '@/dataAccess/rest/apiHooks';

import { isNotFoundError } from '../handleResponseErrors.ts';

export const STREAM_CLIENTS_CACHE_KEY = 'streamClients';

/** mediaserver-api rebuilds its client list every 12 s monitor tick; polling faster gains nothing. */
const STREAM_CLIENTS_REFRESH_MS = 12_000;

/**
 * Every client of every camera stream, from mediaserver-api. One request serves the whole
 * page: cards pick their own streams out of it, and the shared cache key dedupes the hooks.
 *
 * ⚠ `clientId`, `browser` and `platform` are typed optional but arrive as literal `null`
 * when unknown (no `WhenWritingNull` on MediaServer.Api) - test them by truthiness.
 */
export const useViewerClients = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useAuthApiGetSingleFetch<ViewerClientsResponse>({
    baseApiRoute: API_NAMES.mediaserverApi,
    route: StreamStatusEndpoints.clients,
    cacheKeys: [STREAM_CLIENTS_CACHE_KEY],
    returnJsonResult: true,
    enabled,
    // An edge on a mediaserver-api without the route answers 404 every time: stop polling it.
    // A reload asks again, so an upgraded edge is picked up.
    refetchInterval: query =>
      isNotFoundError(query.state.error) ? false : STREAM_CLIENTS_REFRESH_MS,
    staleTime: STREAM_CLIENTS_REFRESH_MS,
    identifier: 'clients',
  });
